import React, { useEffect, useState } from 'react';
import { useTheme } from '../context/ThemeContext';

/**
 * SplashScreen — brand intro.
 *
 * Dark mode: keeps the original deep-indigo gradient, ambient orbs,
 * and heavy glow — it's the signature brand moment.
 *
 * Light mode: switches to a clean near-white background with the same
 * orbs dialed WAY down. Wordmark and logo mark use the same indigo→cyan
 * gradient clip so "Aurx" still reads as a bright piece of brand.
 */
export default function SplashScreen({ onDone }) {
  const [phase, setPhase] = useState('enter');
  const { theme } = useTheme();
  const isLight = theme === 'light';

  useEffect(() => {
    const t1 = setTimeout(() => setPhase('exit'), 1800);
    const t2 = setTimeout(() => onDone(), 2350);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [onDone]);

  /* Theme-conditional styling ─────────────────────────────────────────── */
  const rootBg = isLight
    ? 'radial-gradient(ellipse at 50% 50%, #F5F7FF 0%, #FFFFFF 70%)'
    : 'radial-gradient(ellipse at 50% 50%, #0d1130 0%, #020617 70%)';

  const orb1Color = isLight
    ? 'radial-gradient(circle, rgba(99,102,241,0.10) 0%, transparent 70%)'
    : 'radial-gradient(circle, rgba(99,102,241,0.18) 0%, transparent 70%)';
  const orb2Color = isLight
    ? 'radial-gradient(circle, rgba(139,92,246,0.08) 0%, transparent 70%)'
    : 'radial-gradient(circle, rgba(139,92,246,0.15) 0%, transparent 70%)';
  const orb3Color = isLight
    ? 'radial-gradient(circle, rgba(6,182,212,0.07) 0%, transparent 70%)'
    : 'radial-gradient(circle, rgba(6,182,212,0.12) 0%, transparent 70%)';

  const gridStroke = isLight
    ? 'rgba(99,102,241,0.04)'
    : 'rgba(99,102,241,0.04)';

  const logoBg = isLight
    ? 'linear-gradient(135deg, #FFFFFF 0%, #F5F3FF 100%)'
    : 'linear-gradient(135deg, rgba(99,102,241,0.25) 0%, rgba(139,92,246,0.2) 100%)';
  const logoBorder = isLight ? '1px solid #C7D2FE' : '1px solid rgba(99,102,241,0.4)';
  const logoShadow = isLight
    ? '0 12px 32px rgba(99,102,241,0.18), 0 2px 8px rgba(99,102,241,0.1)'
    : '0 0 40px rgba(99,102,241,0.4), inset 0 1px 0 rgba(255,255,255,0.1)';

  // Logo-mark "A" gradient — toned down in light so it reads as indigo,
  // not neon.
  const letterGradient = isLight
    ? 'linear-gradient(135deg, #4338CA 0%, #7C3AED 50%, #0891B2 100%)'
    : 'linear-gradient(135deg, #a5b4fc 0%, #c084fc 50%, #67e8f9 100%)';
  const letterDropShadow = isLight
    ? 'drop-shadow(0 2px 8px rgba(99,102,241,0.25))'
    : 'drop-shadow(0 0 12px rgba(99,102,241,0.8))';

  // Wordmark gradient
  const wordGradient = isLight
    ? 'linear-gradient(135deg, #1E1B4B 0%, #4338CA 30%, #7C3AED 60%, #0891B2 100%)'
    : 'linear-gradient(135deg, #e0e7ff 0%, #a5b4fc 30%, #c084fc 60%, #67e8f9 100%)';
  const wordDropShadow = isLight
    ? 'drop-shadow(0 4px 20px rgba(99,102,241,0.2))'
    : 'drop-shadow(0 0 30px rgba(99,102,241,0.6))';

  const taglineColor = isLight ? 'rgba(71,85,105,0.75)' : 'rgba(148,163,184,0.8)';
  const progressBg   = isLight ? 'rgba(15,23,42,0.08)' : 'rgba(255,255,255,0.08)';

  return (
    <div
      className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center overflow-hidden transition-all duration-500 ${
        phase === 'exit' ? 'opacity-0 scale-105 pointer-events-none' : 'opacity-100 scale-100'
      }`}
      style={{ background: rootBg }}
    >
      {/* ── Ambient orbs ── */}
      <div className="orb orb-float"
        style={{ width: 600, height: 600, top: '-150px', left: '-150px', background: orb1Color }} />
      <div className="orb"
        style={{ width: 500, height: 500, bottom: '-100px', right: '-100px',
          animation: 'orbFloat 10s ease-in-out infinite reverse', background: orb2Color }} />
      <div className="orb"
        style={{ width: 300, height: 300, top: '30%', right: '10%',
          animation: 'orbFloat 7s ease-in-out 2s infinite', background: orb3Color }} />

      {/* ── Grid overlay ── */}
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: `
          linear-gradient(${gridStroke} 1px, transparent 1px),
          linear-gradient(90deg, ${gridStroke} 1px, transparent 1px)
        `,
        backgroundSize: '64px 64px',
      }} />

      {/* ── Logo block ── */}
      <div className="splash-logo-in relative flex flex-col items-center gap-6">
        {/* Logo mark */}
        <div className="relative">
          {/* Glow ring */}
          <div
            className="absolute inset-0 rounded-3xl animate-pulse-slow"
            style={{
              background: isLight
                ? 'radial-gradient(circle, rgba(99,102,241,0.25) 0%, transparent 65%)'
                : 'radial-gradient(circle, rgba(99,102,241,0.5) 0%, transparent 65%)',
              transform: 'scale(1.6)',
              filter: 'blur(20px)',
            }}
          />
          <div
            className="relative w-24 h-24 rounded-3xl flex items-center justify-center"
            style={{ background: logoBg, border: logoBorder, boxShadow: logoShadow }}
          >
            <span
              className="font-display font-black select-none"
              style={{
                fontSize: 48,
                lineHeight: 1,
                background: letterGradient,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                filter: letterDropShadow,
              }}
            >
              A
            </span>
          </div>
        </div>

        {/* Wordmark */}
        <div className="text-center">
          <h1
            className="font-display font-black tracking-tight select-none"
            style={{
              fontSize: 'clamp(40px, 12vw, 64px)',
              lineHeight: 1,
              background: wordGradient,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              filter: wordDropShadow,
              letterSpacing: '-0.04em',
            }}
          >
            Aurx
          </h1>
        </div>

        {/* Tagline */}
        <p
          className="splash-tagline font-sans text-xs sm:text-sm font-medium tracking-[0.25em] uppercase select-none px-4 text-center break-words"
          style={{ color: taglineColor, letterSpacing: '0.2em', maxWidth: '100%' }}
        >
          Recruitment Intelligence Platform
        </p>

        {/* Progress bar */}
        <div
          className="w-48 h-0.5 rounded-full overflow-hidden mt-2"
          style={{ background: progressBg }}
        >
          <div
            className="splash-bar-fill h-full rounded-full"
            style={{
              background: 'linear-gradient(90deg, #6366f1, #a78bfa, #06b6d4)',
              boxShadow: isLight
                ? '0 0 8px rgba(99,102,241,0.4)'
                : '0 0 10px rgba(99,102,241,0.8)',
            }}
          />
        </div>
      </div>

      {/* ── Corner decorations ── */}
      <div className="absolute top-8 left-8 w-16 h-16"
        style={{
          border: isLight ? '1px solid rgba(99,102,241,0.25)' : '1px solid rgba(99,102,241,0.5)',
          borderRadius: '50% 0',
          transform: 'rotate(45deg)',
          opacity: isLight ? 0.4 : 0.2,
        }} />
      <div className="absolute bottom-8 right-8 w-12 h-12"
        style={{
          border: isLight ? '1px solid rgba(6,182,212,0.25)' : '1px solid rgba(6,182,212,0.5)',
          borderRadius: '0 50%',
          transform: 'rotate(-45deg)',
          opacity: isLight ? 0.3 : 0.15,
        }} />
      <div className="absolute top-1/4 right-8 flex flex-col gap-1">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-0.5 rounded-full" style={{
            width: 20 - i * 4,
            background: isLight ? 'rgba(99,102,241,0.25)' : 'rgba(99,102,241,0.3)',
          }} />
        ))}
      </div>
    </div>
  );
}
